import type { ReactElement } from 'react';
import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Button from '@pray/shared/components/ui/Button';
import { ChevronRight } from '@pray/shared/components/ui/Icons/ChevronRight';
import { cn } from '@pray/shared/utils/styles';

import {
  Albums,
  CDH,
  CRM,
  AI,
  Translate,
  AudioBook,
  Paragraph,
} from 'components/pages/public/components/icons/features';
import { FeatureTitle } from 'components/pages/public/components/ui/FeatureTitle';
import { PRODUCT_LABELS, Products } from 'components/pages/public/types/products';
import { CarouselItem, type CarouselApi } from 'components/ui/carousel';
import useAppRoutes from 'hooks/useAppRoutes';

import type { SolutionsSectionProps } from '../types';
import { CardsCarousel } from './CardsCarousel';

type SolutionCardProps = {
  label: string;
  description: string | React.ReactNode;
  linkText?: string;
  icon: ReactElement;
  linkTo: string;
  className?: string;
  style?: React.CSSProperties;
};

enum Visibility {
  heading = 'heading',
  grid = 'grid',
  carousel = 'carousel',
}

// Component to render PRAYStudio with brand colors
const StyledPrayStudio = () => (
  <>
    <span className="text-pray-secondary">PRAY</span>
    <span className="text-gold-100">Studio</span>
  </>
);

const SolutionCard: React.FC<SolutionCardProps> = ({
  label,
  description,
  linkText,
  icon,
  linkTo,
  className,
  style,
}) => {
  return (
    <Link
      to={linkTo}
      data-tapeventname={`Solutions CTA - ${label}`}
      className={cn(
        'group flex min-h-[216px] gap-6 flex-1 flex-col justify-between rounded-xl border border-[#FFFFFF0D] bg-[#171718] p-5 ease-in-out duration-300 hover:border-gray-600 hover:bg-gray-700 lg:min-w-0',
        className
      )}
      style={style}
    >
      <div className="flex flex-col gap-5">
        <FeatureTitle
          icon={icon}
          title={label}
          className="uppercase"
          iconClassName="size-12 group-hover:before:ease-in-out group-hover:before:duration-300"
        />
        <p className="text-sm font-[450] text-text-secondary lg:text-base">{description}</p>
      </div>
      {linkText && (
        <Button
          data-tapeventname={`Solutions CTA - ${label}`}
          className="flex items-center py-0 pl-0 font-[650] text-gold-100 group-hover:text-gold-200"
        >
          {linkText}
          <ChevronRight
            width={14}
            height={14}
            className="ml-2 text-gold-100 duration-300 group-hover:translate-x-1 group-hover:text-gold-200"
          />
        </Button>
      )}
    </Link>
  );
};

const SolutionsSection: React.FC<SolutionsSectionProps> = ({ solutionsSectionTitle }) => {
  const appRoutes = useAppRoutes();
  const location = useLocation();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  // Refs for elements to observe
  const headingRef = useRef<HTMLHeadingElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // State to track visibility of observed elements
  const [visibility, setVisibility] = useState({
    [Visibility.heading]: false,
    [Visibility.grid]: false,
    [Visibility.carousel]: false,
  });

  // Ref to store the observer instance
  const observerRef = useRef<IntersectionObserver | null>(null);

  const solutionCards = [
    {
      label: PRODUCT_LABELS[Products.SOCIAL_CLIPS],
      description:
        'Transform your sermons into viral social clips. Use AI-powered tools to create engaging, shareable videos and grow your ministry online.',
      linkText: 'LEARN MORE',
      icon: <Albums />,
      linkTo: appRoutes.socialClips(),
    },
    {
      label: PRODUCT_LABELS[Products.MINISTRY_INSIGHTS],
      description: `Empower your ministry with ${
        PRODUCT_LABELS[Products.MINISTRY_INSIGHTS]
      }. Gain audience insights, grow engagement, and streamline social media efforts with advanced analytics and tools.`,
      linkText: 'LEARN MORE',
      icon: <CDH />,
      linkTo: appRoutes.ministryInsights(),
    },
    {
      label: PRODUCT_LABELS[Products.CRM],
      description:
        'Strengthen your ministry and connect with your congregation through emails, announcements, direct messages, and testimonials—all in one easy-to-use platform.',
      linkText: 'LEARN MORE',
      icon: <CRM />,
      linkTo: appRoutes.crm(),
    },
    {
      label: PRODUCT_LABELS[Products.WORKFLOW_AI],
      description: `Empower your ministry with ${
        PRODUCT_LABELS[Products.WORKFLOW_AI]
      }. Automate sermon summaries, social media posts, emails, blogs, and study guides to save time and grow your digital outreach.`,
      linkText: 'LEARN MORE',
      icon: <AI />,
      linkTo: appRoutes.workflowAi(),
    },
    {
      label: PRODUCT_LABELS[Products.AI_TRANSLATIONS],
      description: (
        <>
          Translate your sermons into 30+ languages effortlessly with <StyledPrayStudio />. Preserve your voice, save
          time, and reach a global audience.
        </>
      ),
      linkText: 'LEARN MORE',
      icon: <Translate />,
      linkTo: appRoutes.translations(),
    },
    {
      label: PRODUCT_LABELS[Products.AI_AUDIO_BOOKS],
      description: (
        <>
          Transform your books into audiobooks effortlessly with <StyledPrayStudio />. Use AI to create professional
          audiobooks in your own voice within minutes. Save time and share your message further!
        </>
      ),
      linkText: 'LEARN MORE',
      icon: <AudioBook />,
      linkTo: appRoutes.audioBooks(),
    },
    {
      label: PRODUCT_LABELS[Products.CMS],
      description: (
        <>
          You can store your sermons on <StyledPrayStudio /> to syndicate to Apple Podcast, Spotify, and Pray to reach
          millions globally. Take advantage of AI Search to find any sermons instantly and effortlessly.
        </>
      ),
      linkText: 'LEARN MORE',
      icon: <Paragraph />,
      linkTo: appRoutes.cms(),
    },
  ] as const;

  // Filter out the current page from the solution cards
  const filteredSolutionCards = solutionCards.filter((card) => card.linkTo !== location.pathname);

  useEffect(() => {
    if (!api) {
      return undefined;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', handleSelect);

    // Clean up listener on component unmount
    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  // Observe elements to trigger animations
  useEffect(() => {
    const elementsToObserve = [
      { ref: headingRef, id: Visibility.heading },
      { ref: gridRef, id: Visibility.grid },
      { ref: carouselRef, id: Visibility.carousel },
    ];

    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.3, // Trigger when 30% of the element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const targetId = (entry.target as HTMLElement).dataset.observeId;
        if (entry.isIntersecting && targetId) {
          setVisibility((prev) => ({ ...prev, [targetId]: true }));
          // Unobserve after it becomes visible if you want the animation only once
          if (observerRef.current) {
            observerRef.current.unobserve(entry.target);
          }
        }
      });
    };

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(observerCallback, observerOptions);
    }

    const observer = observerRef.current;

    elementsToObserve.forEach(({ ref, id }) => {
      const element = ref.current;
      // Observe only if the element exists and hasn't become visible yet
      if (element && !visibility[id]) {
        element.dataset.observeId = id; // Add data attribute for identification
        observer.observe(element);
      }
    });

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section className="py-16">
      <div className="mx-auto max-w-7xl">
        <h2
          ref={headingRef}
          className={cn(
            'mb-16 text-left text-[1.75rem] font-bold leading-10 text-pray-secondary opacity-0 lg:text-[2.5rem] lg:leading-snug',
            visibility.heading && 'animate-slide-in-left opacity-1'
          )}
        >
          {solutionsSectionTitle}
        </h2>
        <div ref={gridRef} className="hidden lg:grid lg:grid-cols-3 lg:gap-6">
          {filteredSolutionCards.map((card) => (
            <SolutionCard
              key={`solution-${card.label}`}
              label={card.label}
              description={card.description}
              linkText={card.linkText}
              icon={card.icon}
              linkTo={card.linkTo}
              className={cn('opacity-0', visibility.grid && 'opacity-1 animate-slide-in-down')}
            />
          ))}
        </div>
        <div ref={carouselRef} className="relative -mx-4 pb-16 md:-mx-10 lg:hidden">
          <CardsCarousel count={count} current={current} setApi={setApi} api={api}>
            {filteredSolutionCards.map((card, index) => (
              <CarouselItem
                key={index}
                className={cn(
                  'flex basis-[332px] opacity-0 last:mr-6 md:first:ml-6 md:last:mr-10',
                  visibility.carousel && 'opacity-1 animate-slide-in-right'
                )}
              >
                <SolutionCard
                  label={card.label}
                  description={card.description}
                  linkText={card.linkText}
                  icon={card.icon}
                  linkTo={card.linkTo}
                />
              </CarouselItem>
            ))}
          </CardsCarousel>
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
